import { useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { F0Dialog, F0Text } from '@factorialco/f0-react'
import {
  ExpenseBudgetAllocationFragment,
  FinanceBudgetsDetailBudgetFragment
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'
import { useDeleteBudgetAllocation } from 'modules/finance/hooks/useBudgetAllocations'

interface Props {
  isOpen: boolean
  item: ExpenseBudgetAllocationFragment
  budget: FinanceBudgetsDetailBudgetFragment
  onClose: () => void
  onSuccess: () => void
}

export const RemoveEmployeeModal = ({ isOpen, item, budget, onClose, onSuccess }: Props) => {
  const apolloClient = useApolloClient()
  const [submitting, setSubmitting] = useState(false)
  const { deleteBudgetAllocation } = useDeleteBudgetAllocation({ budgetId: budget.id })

  const handleRemove = async () => {
    setSubmitting(true)
    try {
      const response = await deleteBudgetAllocation({
        budgetId: budget.id,
        ids: [item.id]
      })

      const result = handleResult(response, () => {})

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('budgets.expense_budget_detail.employees.sidepanel.remove_success')
        })

        await apolloClient.refetchQueries({
          include: [
            'FinanceBudgetDetailEmployeesDataCollection',
            'FinanceBudgetDetailRevampPage',
            'FinanceBudgetDetailAmountWidgets'
          ]
        })

        onClose()
        onSuccess()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.error')
        })
      }
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error')
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={i18n.t('budgets.expense_budget_detail.employees.sidepanel.remove_title')}
      primaryAction={{
        label: i18n.t('budgets.expense_budget_detail.employees.sidepanel.remove_button'),
        onClick: handleRemove,
        disabled: submitting,
        loading: submitting,
        variant: 'negative'
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose
      }}
    >
      <F0Text
        variant="body"
        content={i18n.t('budgets.expense_budget_detail.employees.sidepanel.remove_description')}
      />
    </F0Dialog>
  )
}
